/**
 * Tiny client-side image downscale — canvas-resize a large screenshot/photo
 * BEFORE the 2MB attachment gate so retina/4K pastes stop being silently
 * rejected and the Gemini payload shrinks. PNGs with transparency stay PNG;
 * everything else re-encodes to JPEG (much smaller for photos/screenshots).
 *
 * Returns the resized File, or the ORIGINAL file unchanged if it's already
 * small enough / can't be decoded (never throws — intake must not crash).
 */
const MAX_EDGE = 1600; // longest side, px

/** Downscale an image File when its longest edge exceeds MAX_EDGE. */
export async function downscaleImage(file: File): Promise<File> {
  if (!file.type.startsWith("image/")) return file;
  // SVG is vector — rasterising loses fidelity and gains nothing.
  if (file.type === "image/svg+xml") return file;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file; // undecodable → leave it to the existing size gate
  }

  const { width, height } = bitmap;
  const longest = Math.max(width, height);
  if (longest <= MAX_EDGE) {
    bitmap.close?.();
    return file; // already within bounds — no re-encode
  }

  const scale = MAX_EDGE / longest;
  const w = Math.round(width * scale);
  const h = Math.round(height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    bitmap.close?.();
    return file;
  }
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();

  // Keep PNG (preserves transparency); everything else → JPEG at 0.85.
  const isPng = file.type === "image/png";
  const outType = isPng ? "image/png" : "image/jpeg";
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, outType, isPng ? undefined : 0.85),
  );
  if (!blob) return file;

  // If the re-encode somehow got bigger (tiny PNGs can), keep the original.
  if (blob.size >= file.size) return file;

  const dot = file.name.lastIndexOf(".");
  const stem = dot > 0 ? file.name.slice(0, dot) : file.name;
  const ext = isPng ? ".png" : ".jpg";
  return new File([blob], `${stem}${ext}`, { type: outType });
}
